import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { UserRow } from "@/components/user-row";
import { Loader2 } from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { useToast } from "@/hooks/use-toast";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useAuth } from "@/hooks/useAuth";
import type { DocumentUser, Document, DocumentVersion } from "@shared/schema";

const inviteSchema = z.object({
  email: z.string().email("Please enter a valid email address")
});

interface DocumentUsersDialogProps {
  isOpen: boolean;
  onClose: () => void;
  users: DocumentUser[];
  isLoading: boolean;
  documentName: string;
  documentId: string;
  baseUrl: string;
  document: Document;
  currentUser: DocumentUser | null;
  latestVersion: DocumentVersion | null;
}

export function DocumentUsersDialog({
  isOpen,
  onClose,
  users,
  isLoading,
  documentName,
  documentId,
  baseUrl,
  document,
  currentUser,
  latestVersion
}: DocumentUsersDialogProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { isAuthenticated } = useAuth();
  
  const form = useForm<z.infer<typeof inviteSchema>>({
    resolver: zodResolver(inviteSchema),
    defaultValues: { email: "" }
  });

  const inviteMutation = useMutation({
    mutationFn: async (data: z.infer<typeof inviteSchema>) => {
      return apiRequest("POST", `/api/email/invite-enhanced`, {
        recipientEmail: data.email,
        document: {
          id: document.id,
          name: document.name,
          createdAt: document.createdAt
        },
        inviter: {
          name: currentUser?.name || "Document System",
          email: currentUser?.email || ""
        },
        version: {
          number: latestVersion?.version || 1,
          createdAt: latestVersion?.createdAt,
          pageCount: latestVersion?.pageCount || document.pageCount
        },
        collaborators: users.map(user => ({
          name: user.name,
          email: user.email
        })),
        inviteUrl: `${baseUrl}/doc/${documentId}/comment/v/1/p/1?inviteEmail=${encodeURIComponent(data.email)}`,
        organizationName: "Commentt.ai"
      });
    },
    onSuccess: () => {
      toast({
        title: "Invitation sent",
        description: `Invite sent to ${form.getValues().email}`
      });
      form.reset();
    },
    onError: (error: any) => {
      toast({
        title: "Failed to send invite",
        description: error.message || "Please try again",
        variant: "destructive"
      });
    }
  });

  const deleteMutation = useMutation({
    mutationFn: async (userId: string) => {
      return apiRequest("DELETE", `/api/documents/${documentId}/users/${userId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/documents', documentId, 'users'] });
      toast({ title: "User removed from document" });
    },
    onError: () => {
      toast({ title: "Failed to remove user", variant: "destructive" });
    }
  });

  const onSubmit = (data: z.infer<typeof inviteSchema>) => {
    inviteMutation.mutate(data);
  };
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-full max-w-[min(100vw-1.5rem,28rem)] sm:max-w-md p-4 sm:p-6 overflow-hidden">
        <DialogHeader>
          <DialogTitle className="text-base sm:text-lg font-semibold">
            Document Users
          </DialogTitle>
          <DialogDescription className="text-xs sm:text-sm text-muted-foreground">
            Users who have access to "{documentName}"
          </DialogDescription>
        </DialogHeader>
        
        {/* Invite Form */}
        <div className="border-b border-border pb-3 sm:pb-4 mb-3 sm:mb-4">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col sm:flex-row gap-2 w-full">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem className="flex-1 min-w-0">
                    <FormControl>
                      <Input
                        placeholder="Enter email to invite..."
                        {...field}
                        disabled={inviteMutation.isPending}
                        className="w-full"
                        data-testid="input-invite-email"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Button 
                type="submit" 
                disabled={inviteMutation.isPending}
                size="sm"
                style={{ 
                  backgroundColor: document.defaultIndicatorColor,
                  borderColor: document.defaultIndicatorColor 
                }}
                className="text-white hover:opacity-90 transition-opacity w-full sm:w-auto flex-none"
                data-testid="button-send-invite"
              >
                {inviteMutation.isPending ? "Sending..." : "Invite"}
              </Button>
            </form>
          </Form>
        </div>
        
        <div className="overflow-hidden">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-xs sm:text-sm text-muted-foreground">
                No users have joined this document yet.
              </p>
            </div>
          ) : (
            <div className="max-h-[250px] sm:max-h-[400px] overflow-y-auto overflow-x-hidden overscroll-contain">
              <div className="space-y-2 pr-1">
                {users.map((user) => (
                  <UserRow
                    key={user.id}
                    user={user}
                    showClickable={false}
                    onDelete={isAuthenticated ? (user) => deleteMutation.mutate(user.id) : undefined}
                    showDelete={isAuthenticated}
                    documentColor={document.defaultIndicatorColor}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}